import { z } from 'zod';
import { CHANNEL_VALUES } from '../models/session.js';
/**
 * The start-conversation primitive. Start is a first-class node type parallel
 * to Say, Conversation, Form, Transfer, and End Conversation. It marks where a
 * conversation begins, so the beginning of a graph is a row the author sees and
 * configures instead of a property hidden on the entry edge.
 *
 * The editor drew a Start pill before this node existed. The pill was a canvas
 * fiction: it held no settings, and its position rode on the entry edge. A real
 * node carries both.
 */
export const startConversationConfigurationSchema = z.object({
    /**
     * How the conversation opens. The graph's trigger decides which of these an
     * author may pick: a trigger carries a phone variable, an email variable, or
     * both, and a channel with no destination in the event cannot be reached.
     *
     * Empty until the author picks. A graph that runs on nothing is caught at
     * save, not here, so an unfinished graph still opens in the editor.
     */
    channels: z.array(z.enum(CHANNEL_VALUES)).default([]),
    /**
     * Which trigger variable holds where to reach the person. A trigger can
     * declare several numbers, and `Trigger.phoneVariable` holds the first one
     * only, so the author names the one this graph sends to.
     *
     * Null on an inbound graph, which reads no destination from an event, and
     * on a graph saved before the author could choose — the dispatch path then
     * falls back to the trigger's own pointer, which is what it read before.
     */
    destinationVariable: z.string().min(1).max(120).nullable().default(null),
});
export const startConversationNodeSchema = z.object({
    id: z.string().uuid(),
    title: z.string().min(1).max(120),
    type: z.literal('start_conversation'),
    /** Display order within the graph. */
    position: z.number().int().nonnegative(),
    /** Author-visible layout; see nodeLayoutMetadataSchema in graph.ts. */
    layoutMetadata: z
        .object({ position: z.object({ x: z.number(), y: z.number() }) })
        .nullable()
        .optional(),
    ...startConversationConfigurationSchema.shape,
});
//# sourceMappingURL=start-conversation-node.js.map